
import React, { useState, useEffect } from "react";
import {
  Box,
  Grid,
  Card,
  Typography,
  TextField,
  Button,
  Checkbox,
  FormControlLabel,
  IconButton,
  InputAdornment,
  Dialog,
  DialogTitle,
  DialogContent,
  CircularProgress,
  useMediaQuery,
  useTheme,
} from "@mui/material";
import Visibility from "@mui/icons-material/Visibility";
import VisibilityOff from "@mui/icons-material/VisibilityOff";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import ayujalogo from "../Logos/AuthLogo.png";
import { fetchUserFromRedis } from "./Register";
import { useUser } from "../../context/UserContext"; 

const Login = () => {
  const navigate = useNavigate();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
  const isTablet = useMediaQuery(theme.breakpoints.between("sm", "md"));
  const isSmallScreen = useMediaQuery(theme.breakpoints.down("md"));

  const { updateUser } = useUser(); 

  const [showPassword, setShowPassword] = useState(false);
  const [formData, setFormData] = useState({ identifier: "", password: "" });
  const [error, setError] = useState("");
  const [openDialog, setOpenDialog] = useState(false);
  const [loading, setLoading] = useState(false);
  const [user, setUser] = useState(null);

  const loginAPI = process.env.REACT_APP_LOGIN_API;

  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const handleGoogleLogin = () => {
    window.location.href = process.env.REACT_APP_GOOGLE_REDIRECT_API;
  };

  const handleLogin = async () => {
    setLoading(true);
    try {
      const res = await axios.post(loginAPI, formData);
      const userId = res.data.user.user_id;
      const token = res.data.token;
      const userRole = res.data.user.role;
      const approvalStatus = res.data.user.approval_status;
      const isApproved = res.data.user.is_approved;

      if (userRole === "deliveryagent" && res.data.user.agent_id) {
        sessionStorage.setItem("agent_id", res.data.user.agent_id);
        sessionStorage.setItem("agent_role", res.data.user.agent_role);
      }

      if (
        (userRole === "medicineadmin" ||
          userRole === "diagnosticadmin" || userRole === "staff" ||
          userRole === "deliveryagent") &&
        !isApproved
      ) {
        if (approvalStatus === "pending") {
          setError({ message: "Your account is pending admin approval. Please wait for approval." });
        } else if (approvalStatus === "rejected") {
          setError({ message: "Your account has been rejected. Please contact support." });
        } else if (approvalStatus === "blocked") {
          setError({ message: "Your account has been blocked. Please contact support." });
        }
        setOpenDialog(true);
        setLoading(false);
        return;
      }

      // ── Store credentials ──
      sessionStorage.setItem("token", token);
      sessionStorage.setItem("userId", userId);
      sessionStorage.setItem("user", JSON.stringify(res.data.user));

      // ── Fetch full user data from Redis ──
      const userData = await fetchUserFromRedis(userId, token);
      setUser(userData);
      // without needing a page reload
      updateUser(userData || res.data.user);

      if (userRole === "resident") {
        setError({ message: "Residents cannot use this Admin app" });
        setOpenDialog(true);
        return;
      }

      if (userRole === "admin") navigate("/admin-dashboard", { replace: true });
      else if (userRole === "medicineadmin") navigate("/medicine-admin-dashboard", { replace: true });
      else if (userRole === "diagnosticadmin") navigate("/diagnostic-admin-dashboard", { replace: true });
      else if (userRole === "staff") navigate("/english", { replace: true });
      else if (userRole === "deliveryagent") {
        const agentRole = res.data.user.agent_role;
        if (agentRole === "sample_collection") navigate("/PhlebotomistDashboard", { replace: true });
        else navigate("/deliveryagent-dashboard", { replace: true });
      } else {
        navigate("/", { replace: true });
      }
    } catch (err) {
      setError(err.response?.data || { message: "Login failed" });
      setOpenDialog(true);
    } finally {
      setLoading(false);
    }
  };

  const handleCloseDialog = () => {
    setOpenDialog(false);
    setError(null);
  };

  return (
    <>
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          minHeight: "80vh",
          p: { xs: 2, sm: 2, md: 3 },
          backgroundColor: "#fff",
        }}
      >
        <Card
          sx={{
            borderRadius: "16px",
            maxWidth: "1000px",
            width: "100%",
            overflow: "hidden",
            boxShadow: "0 8px 32px rgba(0, 0, 0, 0.1)",
          }}
        >
          <Grid container>

            {/* ── Left Section ── */}
            {!isMobile && (
              <Grid
                item
                xs={12}
                md={6}
                sx={{
                  background: "linear-gradient(135deg, #004f54, #007a85, #007a85)",
                  color: "white",
                  display: "flex",
                  flexDirection: "column",
                  justifyContent: "center",
                  p: isTablet ? 3 : 4,
                  width: isTablet ? "50%" : "60%",
                }}
              >
                <Box sx={{ display: "flex", justifyContent: "center", alignItems: "center", mb: 2 }}>
                  <Box
                    component="img"
                    src={ayujalogo}
                    alt="Ayuja Logo"
                    sx={{ height: isTablet ? "60px" : "80px", width: "auto", display: "block" }}
                  />
                </Box>

                <Typography
                  variant={isTablet ? "h5" : "h4"}
                  sx={{ fontWeight: "bold", mb: 2, textAlign: "center" }}
                >
                  Welcome <br /> Back to Ayuja
                </Typography>

                <Typography
                  variant={isTablet ? "body1" : "h6"}
                  sx={{ mb: 2, textAlign: "center", fontSize: isTablet ? "14px" : undefined }}
                >
                  Your trusted partner in personalized in-home care for every stage of life
                </Typography>

                <Box
                  sx={{
                    display: "flex",
                    justifyContent: "center",
                    alignItems: "center",
                    gap: isTablet ? 1.5 : 3,
                    mb: 4,
                    flexWrap: "wrap",
                  }}
                >
                  <Typography variant="body2" sx={{ display: "flex", alignItems: "center" }}>
                    ✅ 24/7 Support
                  </Typography>
                  <Typography variant="body2" sx={{ display: "flex", alignItems: "center" }}>
                    ✅ Certified Caregivers
                  </Typography>
                </Box>

                <Box
                  sx={{
                    backgroundColor: "rgba(255, 255, 255, 0.1)",
                    borderRadius: "12px",
                    p: isTablet ? 2 : 3,
                    textAlign: "center",
                    width: isTablet ? "80%" : "50%",
                    mx: "auto",
                  }}
                >
                  <Typography variant="subtitle1" fontWeight="bold" mb={1} fontSize={isTablet ? "13px" : undefined}>
                    Emergency Support Available
                  </Typography>
                  <Typography variant="body2" sx={{ mb: 2, fontSize: isTablet ? "12px" : undefined }}>
                    Need immediate assistance? Our emergency support team is available
                    24/7 to help you and your loved ones.
                  </Typography>
                  <Button
                    variant="contained"
                    sx={{
                      backgroundColor: "#80ED99",
                      "&:hover": { backgroundColor: "#80ED99" },
                      color: "#22577A",
                      textTransform: "none",
                      borderRadius: "8px",
                      fontSize: isTablet ? "12px" : undefined,
                    }}
                  >
                    Contact Emergency
                  </Button>
                </Box>
              </Grid>
            )}

            {/* ── Right Section (Login Form) ── */}
            <Grid
              item
              xs={12}
              md={6}
              sx={{
                backgroundColor: "white",
                p: isMobile ? 3 : isTablet ? 3 : 4,
                display: "flex",
                flexDirection: "column",
                justifyContent: "center",
                width: isMobile ? "100%" : isTablet ? "50%" : "40%",
              }}
            >
              {isMobile && (
                <Box sx={{ display: "flex", justifyContent: "center", mb: 2 }}>
                  <Box
                    component="img"
                    src={ayujalogo}
                    alt="Ayuja Logo"
                    sx={{ height: "60px", width: "auto" }}
                  />
                </Box>
              )}

              <Typography
                variant={isMobile ? "h6" : "h5"}
                sx={{ fontWeight: "bold", mb: 1, textAlign: isMobile ? "center" : "left" }}
              >
                Admin Sign In
              </Typography>
              <Typography
                variant="body2"
                sx={{
                  mb: 3,
                  color: "text.secondary",
                  textAlign: isMobile ? "center" : "left",
                }}
              >
                Access your personalized care dashboard
              </Typography>

              <TextField
                label="Email or Phone"
                name="identifier"
                value={formData.identifier}
                onChange={handleChange}
                placeholder="Enter email or 10-digit phone"
                fullWidth
                margin="normal"
                size={isMobile ? "small" : "medium"}
              />

              <TextField
                label="Password"
                name="password"
                value={formData.password}
                onChange={handleChange}
                placeholder="Enter your password"
                type={showPassword ? "text" : "password"}
                fullWidth
                margin="normal"
                size={isMobile ? "small" : "medium"}
                InputProps={{
                  endAdornment: (
                    <InputAdornment position="end">
                      <IconButton onClick={() => setShowPassword(!showPassword)} edge="end">
                        {showPassword ? <VisibilityOff /> : <Visibility />}
                      </IconButton>
                    </InputAdornment>
                  ),
                }}
              />

              <Box
                sx={{
                  display: "flex",
                  justifyContent: "space-between",
                  alignItems: "center",
                  mb: 2,
                  flexWrap: isMobile ? "wrap" : "nowrap",
                }}
              >
                <FormControlLabel
                  control={<Checkbox size={isMobile ? "small" : "medium"} />}
                  label={
                    <Typography variant="body2" fontSize={isMobile ? "13px" : undefined}>
                      Remember me
                    </Typography>
                  }
                />
                <Button
                  variant="text"
                  sx={{
                    mt: isMobile ? 0 : 2,
                    textTransform: "none",
                    fontSize: isMobile ? "13px" : undefined,
                    whiteSpace: "nowrap",
                  }}
                  onClick={() => navigate("/ForgotPassword")}
                >
                  Forgot Password?
                </Button>
              </Box>

              <Button
                variant="contained"
                fullWidth
                sx={{
                  backgroundColor: "#007a85",
                  "&:hover": { backgroundColor: "#005f64" },
                  mb: 3,
                  py: isMobile ? 1 : 1.5,
                  display: "flex",
                  justifyContent: "center",
                  alignItems: "center",
                  fontSize: isMobile ? "14px" : undefined,
                }}
                onClick={handleLogin}
                disabled={loading}
              >
                {loading ? <CircularProgress size={24} color="inherit" /> : "Sign In"}
              </Button>

              <Typography sx={{ textAlign: "center", mb: 2, fontSize: isMobile ? "14px" : undefined }}>
                Don't have an account?{" "}
                <a href="/Register" style={{ color: "#007a85", fontWeight: "bold" }}>
                  Sign Up
                </a>
              </Typography>
            </Grid>
          </Grid>
        </Card>

        {/* ── Error Dialog ── */}
        <Dialog open={openDialog} onClose={handleCloseDialog} fullWidth maxWidth="xs">
          <DialogTitle sx={{ color: "#d32f2f", fontSize: isMobile ? 20 : 26 }}>
            Error
          </DialogTitle>
          <DialogContent>
            <Typography sx={{ fontSize: isMobile ? 16 : 26 }}>
              {error?.message || "Login failed"}
            </Typography>
          </DialogContent>
          <Box sx={{ textAlign: "right", p: 1 }}>
            <Button onClick={handleCloseDialog}>Close</Button>
          </Box>
        </Dialog>
      </Box>
    </>
  );
};

export default Login;